import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';
import { Loader2, CheckCircle2, AlertCircle, Building2, Download } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { ErpService } from '@/services/erpService';
import { onboardingApiClient } from '@/clients/onboardingApiClient';
import { userFacingErrorMessage } from '@/utils/userFacingError';

type FlowStatus = 'loading' | 'selecting_company' | 'installing_extension' | 'completing' | 'success' | 'error';

interface Company {
  id: string;
  name: string;
}

export function ErpCallbackHandler() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const t = useTranslations('consultantTax.erp.callback');
  const [status, setStatus] = useState<FlowStatus>('loading');
  const [message, setMessage] = useState<string>(() => t('processing'));
  const [companies, setCompanies] = useState<Company[]>([]);
  const [sessionId, setSessionId] = useState<string>('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');

  const erpService = new ErpService();

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      // Get OAuth response from URL params
      const code = searchParams.get('code');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (error) {
        throw new Error(errorDescription || error);
      }

      if (!code) {
        throw new Error(t('noCode'));
      }

      setMessage(t('discovering'));

      // Step 1: Get available companies
      const companiesResult = await erpService.getAvailableCompanies(code);

      setSessionId(companiesResult.sessionId);
      setCompanies(companiesResult.companies);

      // Auto-select if only one company
      if (companiesResult.companies.length === 1) {
        setSelectedCompanyId(companiesResult.companies[0].id);
        // Proceed to extension installation
        setStatus('installing_extension');
        setMessage('');
      } else {
        // Show company picker
        setStatus('selecting_company');
        setMessage('');
      }
    } catch (err) {
      console.error('ERP callback error:', err);
      setStatus('error');
      setMessage(userFacingErrorMessage(err, t('failedCompanies')));
    }
  };

  const handleCompleteConnection = async (sessId?: string, compId?: string) => {
    const finalSessionId = sessId || sessionId;
    const finalCompanyId = compId || selectedCompanyId;

    if (!finalSessionId || !finalCompanyId) {
      setStatus('error');
      setMessage(t('selectCompany'));
      return;
    }

    try {
      setStatus('completing');
      setMessage(t('verifying'));

      // Step 2: Complete connection with selected company (includes verification)
      const result = await erpService.completeConnection(finalSessionId, finalCompanyId);

      if (result.success) {
        setMessage(t('updating'));

        // Update ERP connection completion status
        await onboardingApiClient.updateErpConnectionCompletion(true);

        setStatus('success');
        setMessage(
          [
            t('successTitle'),
            '',
            t('successCompany', { company: result.companyName || t('unknownCompany') }),
            t('successSynced', { sales: String(result.salesInvoices || 0), vendor: String(result.vendorInvoices || 0) }),
          ].join('\n')
        );

        // Redirect to overview after a short delay
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        const errorMsg = result.errors?.join(', ') || result.message || t('failedComplete');
        throw new Error(errorMsg);
      }
    } catch (err) {
      console.error('ERP connection error:', err);
      setStatus('error');

      // Check if error is related to extension installation
      const errorMessage = userFacingErrorMessage(err, t('failedErp'));
      if (errorMessage.toLowerCase().includes('extension') || errorMessage.toLowerCase().includes('vendor')) {
        setMessage(t('extensionNotDetected'));
      } else {
        setMessage(errorMessage);
      }
    }
  };

  const handleRetry = () => {
    navigate('/');
  };

  const handleDownloadExtension = () => {
    const link = document.createElement('a');
    link.href = '/extensions/Harmony_Harmony API_2.0.1.1.app';
    link.download = 'Harmony_API_2.0.1.1.app';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('title')}</CardTitle>
          <CardDescription>{t(`description.${status}`)}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'loading' && (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
              <p className="text-sm text-muted-foreground text-center">
                {message}
              </p>
            </div>
          )}

          {status === 'selecting_company' && (
            <div className="space-y-4">
              <div className="rounded-lg border bg-muted/50 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="h-5 w-5 text-primary" />
                  <h3 className="text-sm font-semibold">{t('availableCompanies')}</h3>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                  {t('pickHint')}
                </p>
                <RadioGroup value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                  <div className="space-y-2">
                    {companies.map((company) => (
                      <div key={company.id} className="flex items-center space-x-2">
                        <RadioGroupItem value={company.id} id={company.id} />
                        <Label htmlFor={company.id} className="flex-1 cursor-pointer">
                          {company.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </RadioGroup>
              </div>
              <Button
                onClick={() => {
                  if (selectedCompanyId) {
                    setStatus('installing_extension');
                  }
                }}
                disabled={!selectedCompanyId}
                className="w-full"
              >
                {t('continueToExtension')}
              </Button>
            </div>
          )}

          {status === 'installing_extension' && (
            <div className="space-y-4">
              <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <Download className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <div className="space-y-2 flex-1">
                    <p className="text-sm font-semibold text-purple-900">
                      {t('installTitle')}
                    </p>
                    <p className="text-xs text-purple-800">
                      {t('installIntro')}
                    </p>
                    <ol className="text-xs text-purple-800 space-y-1 list-decimal list-inside pl-2">
                      <li>{t('steps.download')}</li>
                      <li>{t('steps.search')}</li>
                      <li>{t('steps.manage')}</li>
                      <li>{t('steps.select')}</li>
                      <li>{t('steps.accept')}</li>
                    </ol>
                    <Button
                      onClick={handleDownloadExtension}
                      variant="outline"
                      size="sm"
                      className="mt-2 bg-white hover:bg-purple-50 border-purple-300"
                    >
                      <Download className="mr-2 h-3 w-3" />
                      {t('download')}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-xs text-blue-900">
                  {t.rich('important', { strong: (chunks) => <strong>{chunks}</strong> })}
                </p>
              </div>

              <Button
                onClick={() => handleCompleteConnection()}
                className="w-full"
                size="lg"
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                {t('installed')}
              </Button>
            </div>
          )}

          {status === 'completing' && (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
              <p className="text-sm text-muted-foreground text-center">
                {message}
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="flex flex-col items-center justify-center py-8">
              <CheckCircle2 className="h-12 w-12 text-green-500 mb-4" />
              <p className="text-sm text-center whitespace-pre-line">
                {message}
              </p>
              <p className="text-xs text-muted-foreground text-center mt-2">
                {t('redirecting')}
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <div className="flex flex-col items-center justify-center py-8">
                <AlertCircle className="h-12 w-12 text-destructive mb-4" />
                <p className="text-sm text-center text-destructive">
                  {message}
                </p>
              </div>
              <Button onClick={handleRetry} className="w-full">
                {t('returnToOverview')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ErpCallbackHandler;
